import React from 'react';
import { useTranslation } from 'react-i18next';
import { Utils } from '../../services';
import { Setting } from '../../models';
import { Col, CardTitle, CardText, FormGroup, Label, Button, Row } from 'reactstrap';
import * as _ from 'es-toolkit';
import { useSelector } from 'react-redux';

import HelpTooltips from '../HelpTooltips/HelpTooltips';
import HelpInfoAlert from '../HelpInfoAlert/HelpInfoAlert';

import './SettingField.css';

const SettingField = (props) => {
    const user = useSelector(state => state.auth.user);
    let {
        name, setting, showTitle, level, overrides,
        tickettypes, salepoints, articles, layouts,
        articleCategories, postCategories, places,
        onDeleteFromSalePoint
    } = props;
    const { t } = useTranslation();

    level = level || 0;
    if (!name || !setting)
        return null;

    const nameForlabel = name?.replace('.production', '').replace('.preprod', '').replace('.testing', '');

    /**
     * Locales are dotified flat keys (keySeparator: false). Some sections only
     * define settings.<path>.title (e.g. edition) while field labels use
     * settings.<path>. Fall back to .title when the base key is missing.
     */
    const settingLabel = (path) => {
        const baseKey = `settings.${path}`;
        const resolved = t(baseKey);
        return resolved === baseKey ? t(`${baseKey}.title`) : resolved;
    };

    const isTranslatableField = value => {
        return _.isPlainObject(value) && (
            (Object.keys(value).includes('fr') && !_.isPlainObject(value.fr)) ||
            (Object.keys(value).includes('en') && !_.isPlainObject(value.en)) ||
            (Object.keys(value).includes('de') && !_.isPlainObject(value.de)) ||
            (Object.keys(value).includes('it') && !_.isPlainObject(value.it))
        );
    };

    const hasSubSettings = value => {
        return _.isPlainObject(value);
    };

    const { input, role, hideLabel, helpTooltip } = Setting.propertyForm(t, setting, name, tickettypes, salepoints, articles, articleCategories, postCategories, places, layouts, overrides);
    const helpTipId = helpTooltip ? `settingTip-${name.replace(/\./g, '_')}` : null;
    const value = Utils.getDotified(setting, name);

    if (!input && !isTranslatableField(value) && value && hasSubSettings(value)) {
        const orderedChildKeys = _.sortBy(
            Object.keys(value),
            (key) => {
                const { order } = Setting.propertyForm(
                    t,
                    setting,
                    `${name}.${key}`,
                    tickettypes,
                    salepoints,
                    articles,
                    articleCategories,
                    postCategories,
                    places,
                    layouts,
                    overrides
                );
                return order;
            }
        );

        return (
            <div className="SettingField">
                {(user?.isSuperAdmin() || (!role && user?.can('editSelf', 'settings'))) &&
                    <>
                        {(showTitle || (onDeleteFromSalePoint && level > 0)) && (
                            <Row className="align-items-center mb-1">
                                {showTitle && (
                                    <Col>
                                        <CardTitle className="small mb-0">{!hasSubSettings(value) ? settingLabel(nameForlabel) : t(`settings.${nameForlabel}.title`)}</CardTitle>
                                    </Col>
                                )}
                                {onDeleteFromSalePoint && level > 0 && (
                                    <Col xs="auto" className={showTitle ? '' : 'ml-auto'}>
                                        <Button color="danger" size="sm" onClick={() => onDeleteFromSalePoint(name)} title={t('common.delete')}>
                                            <i className="fa fa-times" />
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        )}
                        <div className={`wrapper-${level}`}>
                            {name === 'retention.posthog' && (
                                <HelpInfoAlert helpId="settings.retention.posthog">
                                    {t('settings.retention.posthog_intro')}
                                </HelpInfoAlert>
                            )}

                            <CardText tag="div">
                                {orderedChildKeys.map((key) => (
                                    <SettingField
                                        {...props}
                                        key={key}
                                        showTitle
                                        name={`${name}.${key}`}
                                        level={level + 1}
                                    />
                                ))}
                            </CardText>
                        </div>
                    </>
                }
            </div>
        );
    }

    if (!input)
        return null;

    const cols = [];
    if (hideLabel && !onDeleteFromSalePoint)
        cols.push(12, 12, 12);
    else if (!hideLabel && !onDeleteFromSalePoint)
        cols.push(12, 8, 9);
    else
        cols.push(10, 7, 8);

    return (
        <>
            {(user?.isSuperAdmin() || (!role && user?.can('editSelf', 'settings'))) &&
                <div className="SettingField">
                    <FormGroup row>
                        {!hideLabel && (
                            <Label for="_id" lg={4} xl={3}>
                                {settingLabel(nameForlabel)}
                            </Label>
                        )}
                        <Col
                            xs={cols[0]}
                            sm={cols[0]}
                            md={cols[0]}
                            lg={cols[1]}
                            xl={cols[2]}
                            data-tip={helpTooltip ? '' : undefined}
                            data-for={helpTipId || undefined}
                        >
                            {input}
                            {helpTooltip && <HelpTooltips id={helpTipId} value={helpTooltip} />}
                        </Col>
                        {onDeleteFromSalePoint &&
                        <Col xs={2} sm={2} md={2} lg={1} xl={1}>
                            <Button color="danger" size="sm" className='float-right' onClick={() => onDeleteFromSalePoint(name)}><i className='fa fa-times'></i></Button>
                        </Col>
                        }
                    </FormGroup>
                </div>
            }
        </>
    );
}

export default SettingField;
